import React, { useState, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import PageTitle from '@/components/common/PageTitle';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Messages, Role } from '@/common/constants';
import Loading from '@/components/common/Loading';
import CompanyService from '@/services/Company.service';
import Employee, { EmployeeRequest } from '@/types/Employee.type';
import { Department, Departments } from '@/types/Department.type';
import DepartmentService from '@/services/Department.service';
import EmployeeService from '@/services/Employee.service';
import { RelationRequest } from '@/types/DbCommon.type';
import URL from '@/common/routes/Url';
import CommonActionButton from '@/components/common/CommonActionButton';

type EmployeeForm = Employee & Departments & { password_confirmation: string };

const EmployeeDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const useFormMethods = useForm<EmployeeForm>({ shouldUnregister: false });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    getValues,
  } = useFormMethods;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [department, setDepartment] = useState<Department[] | []>([]);
  const [loading, setLoading] = useState(true);
  const [contPlan, setContPlan] = useState(0);

  const inputs: Array<HookFormControlsProps> = [
    {
      name: 'name',
      type: 'text',
      label: '社員名*',
      rule: {
        required: '氏名は必須項目です。',
        maxLength: {
          value: 20,
          message: '氏名は20文字以内で入力してください。',
        },
      },
    },
    {
      name: 'email',
      type: 'text',
      label: 'メールアドレス*',
      rule: {
        required: 'メールアドレスは必須項目です。',
        maxLength: {
          value: 255,
          message: 'メールアドレスは255文字以内で入力してください。',
        },
        pattern: {
          value: new RegExp(
            "^[a-zA-Z0-9\\.\\!\\#\\$\\%\\&\\'\\*\\+\\/\\=\\\\\\?\\^\\_\\`\\{\\|\\}\\~\\-]+\\@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$",
          ),
          message: 'メールアドレスの形式が不正です。',
        },
      },
    },
    // {
    //   name: 'password',
    //   type: 'password',
    //   label: 'パスワード*　追加・変更時のみ入力してください',
    //   helper:
    //     '※パスワードは8～99文字の半角小文字＋大文字＋数字を組み合わせてください。特殊文字を含める場合は^$*.[]{}()?"!@#%&/\\,><\':;|_~`=+-が利用可能です。',
    //   rule: {
    //     required: !pathParams.id ? 'パスワードは必須項目です。' : undefined,
    //     pattern: {
    //       value: new RegExp(
    //         '^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])[a-zA-Z0-9\\^\\$\\*\\.\\[\\]\\{\\}\\(\\)\\?\\"\\!\\@\\#\\%\\&\\/\\\\,\\>\\<\\\'\\:\\;\\|\\_\\~\\`\\=\\+\\-]*$',
    //       ),
    //       message: 'パスワードは半角小文字＋大文字＋数字を組み合わせてください。',
    //     },
    //     minLength: {
    //       value: 8,
    //       message: 'パスワードは8文字以上で入力してください。',
    //     },
    //     maxLength: {
    //       value: 99,
    //       message: 'パスワードは99文字以下で入力してください。',
    //     },
    //   },
    // },
    // {
    //   name: 'password_confirmation',
    //   type: 'password',
    //   label: 'パスワード（確認）*　追加・変更時のみ入力してください',
    //   rule: {
    //     required: !pathParams.id ? 'パスワード（確認）は必須項目です。' : undefined,
    //     validate: (value) => value === getValues('password') || 'パスワードとパスワード（確認）の入力が一致しません。',
    //   },
    // },
    {
      name: 'departments',
      type: 'autocomplete',
      label: '部署*',
      options: department,
      getOptionLabel: 'name',
      rule: {
        required: '部門は必須項目です。',
      },
      autocompleteProps: {
        multiple: true,
      },
    },
    {
      name: 'role',
      type: 'select',
      label: '権限*',
      options: contPlan === 1 ? Role : {管理者: 1, 一般: 10},
      rule: {
        required: '権限は必須項目です。',
      },
    },
    {
      name: 'phoneNumber',
      type: 'text',
      label: '携帯電話',
      rule: {
        maxLength: {
          value: 15,
          message: '携帯電話は15字以内で入力してください。'
        },
        /*pattern: {
          value: new RegExp('^0[0-9]{9,14}$'),
          message: '携帯電話は0から始まる、10~15桁の数字で入力してください。',
        },*/
      },
    },
    {
      name: 'extensionNumber',
      type: 'text',
      label: '内線番号',
      rule: {
        maxLength: {
          value: 15,
          message: '内線番号は15文字以内で入力してください。',
        },
        /*pattern: {
          value: new RegExp('^[0-9]+$'),
          message: '内線番号は数字で入力してください。',
        },*/
      },
    },
  ];

  useLayoutEffect(() => {
    (async () => {
      await CompanyService.get().then((response: { data: any }) => {
        setContPlan(response.data.contPlan);
      });
      await DepartmentService.getAll().then((response: { data: Department[] }) => {
        setDepartment(response.data);
      });
      if (!pathParams.id) {
        setLoading(false);
        return;
      }
      await EmployeeService.get(pathParams.id)
        .then((response: { data: EmployeeForm }) => {
          response.data.extensionNumber = response.data.extensionNumber ?? '';
          response.data.phoneNumber = response.data.phoneNumber ?? '';
          reset(response.data);
        })
        .catch((_e) => {
          setLoading(false);
          enqueueSnackbar(Messages.select.failure);
        });
      setLoading(false);
    })();
  }, []);

  const onSubmit = async (values: EmployeeForm) => {
    try {
      const departments: RelationRequest = values.departments.map((department) => {
        return department['id'];
      });
      const request: EmployeeRequest = { ...values, ...{ department_id: departments } };

      if (pathParams.id) {
        await EmployeeService.update(request, pathParams.id);
        enqueueSnackbar(Messages.update.success, { variant: 'info' });
      } else {
        await EmployeeService.create(request);
        enqueueSnackbar(Messages.create.success, { variant: 'info' });
      }
      navigate(URL.employees);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12} md={8} lg={4}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>社員マスタ ー 編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {inputs.map((input, index) => (
                  <Grid item key={index} xs={12}>
                    <HookFormControls key={index} {...input} />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <CommonActionButton isSubmitting={isSubmitting} />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default EmployeeDetail;
